import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Paper,
  Slider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "../../../components/DateInput";
import { DateTime, Duration } from "luxon";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import LoadingButton from "@mui/lab/LoadingButton";
import { FormItem } from "../../../components/FormItem";
import { ControlledTextbox } from "../../../components/FormComponents";
import Box from "@mui/material/Box";
import { FetchedPlayback } from "./ReplayList";
import { ArrowBack } from "@mui/icons-material";
import { useAppSelector } from "../../hooks/reduxHooks";
import TimezoneSelect from "react-timezone-select";
import {
  isAmpmTimezone,
  getFormattedDateTime,
  getDateOrderForTimezone,
  getDateFormatString,
} from "../../utils/dateTimeUtils";
import { Config } from "../../config/config";

export function CreateReplay(props: { doClose?: Function; config: Config }) {
  const maxLength = useMemo(() => {
    return props.config?.replayMaxDurationMinutes ?? 30;
  }, [props.config]);

  const defaultMetadata = useMemo(
    () => ({
      name: "",
      createdBy: "",
      createdAt: DateTime.now().startOf("minute"),
      notes: "",
      duration: 0, // Default to 0 minutes
    }),
    []
  );
  const [metadata, setMetadata] = useState(defaultMetadata);

  // Add effect to keep createdAt updated to current minute
  useEffect(() => {
    // Update createdAt every 10 seconds to keep it current
    const interval = setInterval(() => {
      setMetadata((prev) => ({
        ...prev,
        createdAt: DateTime.now().startOf("minute"),
      }));
    }, 10000);

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, []);

  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Get current time (updated every render)
  const currentTime = useMemo(() => DateTime.now().startOf("minute"), []);

  // Track current time in state and update it periodically
  const [now, setNow] = useState(currentTime);

  // Update current time every 10 seconds to keep max duration fresh
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setNow(DateTime.now().startOf("minute"));
    }, 10000);

    return () => clearInterval(timeInterval);
  }, []);

  // Default start time: current time minus maxLength minutes (but cap at 0)
  const defaultStartTime = useMemo(() => {
    const proposedStartTime = now.minus({ minutes: maxLength });
    return proposedStartTime < DateTime.fromMillis(0)
      ? DateTime.fromMillis(0)
      : proposedStartTime;
  }, [now, maxLength]);

  // Start date initialized to default but can be changed by user
  const [startDate, setStartDate] = useState(defaultStartTime);

  // Calculate maximum possible duration based on start time and current time
  const maxPossibleDuration = useMemo(() => {
    // Max duration is the time between start date and current time
    const diffMs = now.diff(startDate).milliseconds;

    // Ensure positive value (in case start date was manually set to future)
    const positiveDiffMs = Math.max(0, diffMs);

    // Cap at the configured maximum duration
    return Math.min(positiveDiffMs, maxLength * 60000);
  }, [startDate, now, maxLength]);

  // End date derived from start date + duration
  const endDate = useMemo(() => {
    // If duration is 0, end date should equal start date
    if (metadata.duration === 0) {
      return startDate;
    }

    const calculatedEnd = startDate.plus({ milliseconds: metadata.duration });

    // Ensure end date doesn't exceed current time
    return calculatedEnd > now ? now : calculatedEnd;
  }, [startDate, metadata.duration, now]);

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState({ info: false, booking: false });

  const [replayInfo, setReplayInfo] = useState({
    start: 0,
    end: 0,
    counts: {},
  });

  // Load replay info from API
  const loadReplay = (start, end) => {
    setIsLoading((state) => ({ ...state, info: true }));
    getReplayInfo(start, end)
      .then((res) => {
        setReplayInfo(res.data);
        setIsLoading((state) => ({ ...state, info: false }));
      })
      .catch((error) => {
        setErrorMessage(error.response.statusText + ": " + error.response.data);
        setIsLoading((state) => ({ ...state, info: false }));
      });
  };

  const [isSaving, setIsSaving] = useState(false);

  const saveReplay = async () => {
    const startUTC = startDate.startOf("minute").toUTC();
    const endUTC = endDate.startOf("minute").toUTC();

    setIsSaving(true);
    setErrorMessage("");

    try {
      const response = await createReplay(
        metadata.name,
        startUTC,
        endUTC,
        metadata.createdBy,
        metadata.notes,
        selectedTimezone
      );

      const data = response.data;
      if (data.success === false) {
        setErrorMessage(data.error || "Replay creation failed.");
      } else {
        // Success — close the modal
        if (props.doClose) props.doClose();
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage("An error occurred while creating the replay.");
      }
      console.error("Replay creation error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const [replayQueries, setReplayQueries] = useState([]);
  // const fetchBookingReplayQueries = (start, end) => {
  //     // setReplayQueries([])
  //     setIsLoading(state => ({...state, booking: true}))
  //     if (replayQueries.length === 0) {
  //         getBookingQueries(start, end).then(res => setReplayQueries(res.data)).catch(error => setErrorMessage(error.response.data))
  //     } else {
  //         setReplayQueries([])
  //     }
  // }
  //
  // const [barrierRequestQueries, setBarrierRequestQueries] = useState([])
  // const fetchBarrierRequestReplayQueries = (start, end) => {
  //     // setReplayQueries([])
  //     setIsLoading(state => ({...state, barrierrequest: true}))
  //     if (replayQueries.length === 0) {
  //         getBarrierRequestQueries(start, end).then(res => setReplayQueries(res.data)).catch(error => setErrorMessage(error.response.data))
  //     } else {
  //         setReplayQueries([])
  //     }
  // }

  //   useEffect(() => {
  //     setIsLoading({ info: false, booking: false });
  //   }, [replayInfo, replayQueries]);

  // if (endDate > startDate.plus({minute: 30})) {
  //     console.warn("end date too far in the future")
  // }
  const [durationClicks, setDurationClicks] = useState(0);

  const isDataCurrent = useMemo(
    () =>
      replayInfo.end === endDate.toMillis() &&
      replayInfo.start === startDate.toMillis(),
    [replayInfo, startDate, endDate]
  );

  // When start date changes, update duration to stay within bounds
  useEffect(() => {
    // Check if current duration exceeds max possible duration
    if (metadata.duration > maxPossibleDuration) {
      // If so, cap it at the max
      setMetadata((prev) => ({
        ...prev,
        duration: maxPossibleDuration,
      }));
    }
  }, [startDate, now, maxPossibleDuration, metadata.duration]);

  const allowSubmit = useMemo(() => {
    const requiredFieldsFilled =
      metadata.name.length > 0 &&
      metadata.createdBy.length > 0 &&
      metadata.notes.length > 0;

    // End date must be after start date (which requires at least 1 minute duration)
    const hasMinimumDuration = metadata.duration >= 60000; // 1 minute minimum for submission

    return requiredFieldsFilled && hasMinimumDuration;
  }, [metadata]);

  const replayLoaded = useMemo(() => replayInfo.end !== 0, [replayInfo]);
  const allowSave = useMemo(
    () => replayLoaded && isDataCurrent,
    [replayLoaded, isDataCurrent]
  );

  const doButtonClick = useCallback(() => {
    if (allowSave) {
      saveReplay();
    } else {
      loadReplay(startDate, endDate);
    }
  }, [allowSave, saveReplay, startDate, endDate]);

  const doClose = () => {
    if (props.doClose) props.doClose();
  };

  const permissions = useAppSelector((state) => state.permissions);

  // Format duration to show minutes (handles 0 case properly)
  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    return `${minutes} min`;
  };

  return (
    <Paper sx={{ paddingTop: 0.6 }}>
      <Grid container alignItems={"center"}>
        {props.doClose && (
          <IconButton
            // sx={{ position: "fixed" }}
            color={"primary"}
            onClick={() => doClose()}
          >
            <ArrowBack />
          </IconButton>
        )}
        <Typography
          sx={{ margin: "auto" }}
          fontWeight={"bold"}
          fontSize={"large"}
          onClick={() => {
            if (permissions.textid === "sysAdmin") {
              setDurationClicks((prev) => prev + 1);
            }
          }}
        >
          Create New Replay
        </Typography>
      </Grid>
      <Box sx={{ padding: 1.2 }}>
        <Grid container justifyContent="center">
          <Grid
            item
            xs={12}
            md={Object.keys(replayInfo.counts).length > 0 ? 6 : 12} // Use full width if no summary
            container
            spacing={1}
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Box sx={{ display: "block", width: "100%" }}>
              <ControlledTextbox
                value={metadata.name}
                updateValue={(name, value) =>
                  setMetadata((prevState) => ({ ...prevState, name: value }))
                }
                name={"name"}
              />
              <FormItem>
                <FormControl fullWidth sx={{ maxWidth: "100%" }}>
                  <FormLabel>Timezone</FormLabel>
                  <TimezoneSelect
                    value={selectedTimezone}
                    onChange={(tz) => {
                      setSelectedTimezone(tz.value);
                      setStartDate((currentStartDate) =>
                        currentStartDate.setZone(tz.value).startOf("minute")
                      );
                      setMetadata((prev) => ({
                        ...prev,
                        createdAt: prev.createdAt
                          .setZone(tz.value)
                          .startOf("minute"),
                      }));
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                        fontFamily: "Roboto, sans-serif",
                      }),
                      control: (base) => ({
                        ...base,
                        fontFamily: "Roboto, sans-serif",
                        overflow: "hidden",
                      }),
                      option: (base) => ({
                        ...base,
                        fontFamily: "Roboto, sans-serif",
                      }),
                      input: (base) => ({
                        ...base,
                        fontFamily: "Roboto, sans-serif",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontFamily: "Roboto, sans-serif",
                        maxWidth: "80%",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }),
                    }}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormControl fullWidth>
                  <DateTimePicker
                    date={startDate.setZone(selectedTimezone, {
                      keepLocalTime: true,
                    })}
                    setDate={(newDate: DateTime) => {
                      if (newDate) {
                        // Ensure start date doesn't exceed current time
                        const snappedDate = newDate.startOf("minute");
                        const validDate = snappedDate > now ? now : snappedDate;
                        setStartDate(validDate);
                      }
                    }}
                    name={"start"}
                    label={"Start Date (Minute)"}
                    ampm={isAmpmTimezone(selectedTimezone)}
                    maxDateTime={now}
                    timezone={selectedTimezone}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormControl fullWidth>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs>
                      <FormLabel>Duration</FormLabel>
                      <Slider
                        size={"small"}
                        min={0} // Allow 0 minutes minimum
                        max={maxPossibleDuration}
                        step={60000} // Step by 1 minute
                        value={Math.min(metadata.duration, maxPossibleDuration)}
                        valueLabelFormat={formatDuration}
                        valueLabelDisplay="auto"
                        onChange={(_, value) => {
                          if (typeof value === "number") {
                            // Snap to minute intervals
                            const snappedValue =
                              Math.floor(value / 60000) * 60000;
                            setMetadata((prev) => ({
                              ...prev,
                              duration: snappedValue,
                            }));
                          }
                        }}
                        sx={{ width: "100%", touchAction: "none" }}
                      />
                    </Grid>
                    <Grid xs={3.25} item>
                      <Input
                        value={Math.floor(metadata.duration / 60000)} // Show whole minutes
                        size="small"
                        onChange={(event) => {
                          const newDurationMinutes = parseInt(
                            event.target.value,
                            10
                          );
                          if (!isNaN(newDurationMinutes)) {
                            // Convert minutes to milliseconds
                            const newDurationMs = newDurationMinutes * 60000;

                            // Ensure it's not negative
                            const nonNegativeMs = Math.max(0, newDurationMs);

                            // Cap at maximum possible duration
                            const finalDuration = Math.min(
                              maxPossibleDuration,
                              nonNegativeMs
                            );

                            // Update metadata with the new duration
                            setMetadata((prev) => ({
                              ...prev,
                              duration: finalDuration,
                            }));
                          }
                        }}
                        inputProps={{
                          step: 1, // Step by whole minutes
                          min: 0, // Allow 0 minimum
                          max: Math.floor(maxPossibleDuration / 60000),
                          type: "number",
                          "aria-labelledby": "input-slider",
                        }}
                      />
                      <Typography variant="caption" display="block">
                        minutes
                      </Typography>
                    </Grid>
                  </Grid>
                </FormControl>
              </FormItem>
              <FormItem>
                <FormControl fullWidth>
                  <DateTimePicker
                    disabled
                    date={endDate.setZone(selectedTimezone, {
                      keepLocalTime: true,
                    })}
                    setDate={() => {}}
                    name={"end"}
                    label={"End Date (Calculated)"}
                    ampm={isAmpmTimezone(selectedTimezone)}
                    minDateTime={startDate.plus({ minutes: 1 })}
                    maxDateTime={now}
                    timezone={selectedTimezone}
                  />
                </FormControl>
              </FormItem>
              <ControlledTextbox
                value={metadata.createdBy}
                updateValue={(name, val) =>
                  setMetadata((prev) => ({ ...prev, createdBy: val }))
                }
                name={"Created By"}
              />
              <FormItem>
                <TextField
                  label={"Notes"}
                  multiline={true}
                  maxRows={3}
                  minRows={1}
                  fullWidth
                  size={"small"}
                  name={"notes"}
                  value={metadata.notes}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </FormItem>
              {/*<ControlledTextbox value={metadata.notes} type={''}*/}
              {/*               updateValue={(name, val) => setMetadata(prev => ({...prev, notes: val}))}*/}
              {/*               name={"Notes"}/>*/}
              <FormItem>
                <FormControl fullWidth>
                  <DateTimePicker
                    disabled={true}
                    date={metadata.createdAt.setZone(selectedTimezone)}
                    setDate={() => {}}
                    name={"created_at"}
                    label={"Created At (Current Minute)"}
                    ampm={isAmpmTimezone(selectedTimezone)}
                    timezone={selectedTimezone}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <LoadingButton
                  variant={"contained"}
                  loading={allowSave ? isSaving : isLoading.info}
                  disabled={isSaving || isLoading.info || !allowSubmit}
                  color={allowSave ? "success" : "primary"}
                  onClick={() => doButtonClick()}
                >
                  {allowSave ? "Save" : "Preview"} Replay
                </LoadingButton>
                <Button
                  variant={"outlined"}
                  disabled={!allowSave}
                  onClick={() => {
                    // setMetadata(defaultMetadata)
                    setReplayInfo((prev) => ({
                      ...prev,
                      start: 0,
                      end: 0,
                      counts: {},
                    }));
                  }}
                >
                  Reset
                </Button>
              </FormItem>
            </Box>
            {/*<Grid item>*/}
            {/*    <LoadingButton variant={'outlined'} loading={isLoading.booking}*/}
            {/*                   onClick={() => fetchBookingReplayQueries(startDate, endDate)}>{replayQueries.length > 0 ? "Hide" : "Show"} Booking*/}
            {/*        Replay</LoadingButton>*/}
            {/*</Grid>*/}
            {/*<Grid item>*/}
            {/*    <LoadingButton variant={'outlined'} loading={isLoading.booking}*/}
            {/*                   onClick={() => fetchBarrierRequestReplayQueries(startDate, endDate)}>{replayQueries.length > 0 ? "Hide" : "Show"} Barrier*/}
            {/*        Request</LoadingButton>*/}
            {/*</Grid>*/}
          </Grid>
          {Object.keys(replayInfo.counts).length > 0 && (
            <Grid
              item
              xs={12}
              md={6}
              container
              spacing={1}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Typography>Summary</Typography>
              <TableContainer>
                <Table size={"small"}>
                  <TableRow>
                    <TableCell>Start</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {getFormattedDateTime(
                        DateTime.fromMillis(replayInfo.start),
                        selectedTimezone,
                        false // Don't include seconds
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>End</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {getFormattedDateTime(
                        DateTime.fromMillis(replayInfo.end),
                        selectedTimezone,
                        false // Don't include seconds
                      )}
                    </TableCell>
                  </TableRow>
                  {Object.entries(replayInfo?.counts).map((entry) => (
                    <TableRow key={entry[0]}>
                      <TableCell>{`${entry[0]}`}</TableCell>
                      <TableCell>{`${entry[1]}`}</TableCell>
                    </TableRow>
                  ))}
                </Table>
              </TableContainer>
            </Grid>
          )}
          {/*<Paper sx={{margin: 1}}>*/}
          {/*    {replayQueries.length > 0 && <ReplayQueryTable data={replayQueries}/>}*/}
          {/*</Paper>*/}
          <Snackbar
            open={!!errorMessage}
            autoHideDuration={10000}
            onClose={() => setErrorMessage("")}
          >
            <Alert severity={"error"}>{errorMessage}</Alert>
          </Snackbar>
        </Grid>
      </Box>
    </Paper>
  );
  // return <><DraggableWindow position={{ x: 0, y: 100 }} open={props.open} onClose={() => console.log()} title={'Replay Controls'} >
  //     <p>Placeholder</p>
  // </DraggableWindow></>
}

function ReplayQueryTable(props: { data: { delay: number; query: string }[] }) {
  return (
    <TableContainer>
      <Table size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell>Delay</TableCell>
            <TableCell>Query</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.data.map((value, i) => {
            const duration = Duration.fromMillis(value.delay);
            const hms = duration.toFormat("hh mm ss").split(" ");
            return (
              <TableRow key={"query" + i}>
                <TableCell>
                  {hms[0] + "h" + hms[1] + "m" + hms[2] + "s"}
                </TableCell>
                <TableCell>{value.query}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}



async function createReplay(
  name: string,
  start: DateTime,
  end: DateTime,
  createdBy: string,
  notes: string,
  selectedTimezone: string
) {
  // Ensure dates are snapped to minutes
  const snappedStart = start.startOf("minute");
  const snappedEnd = end.startOf("minute");
  // Set createdAt to current minute in time
  const currentTime = DateTime.now().startOf("minute").toMillis();

  console.log("Creating replay with snapped times:", {
    start: snappedStart.toISO(),
    end: snappedEnd.toISO(),
    createdAt: DateTime.fromMillis(currentTime).toISO(),
  });

  const replay: FetchedPlayback = {
    name: name,
    start: snappedStart.toMillis(), // Use snapped millis
    end: snappedEnd.toMillis(), // Use snapped millis
    createdBy,
    createdAt: currentTime, // Use the current minute time instead of Date.now()
    notes,
    timezone: selectedTimezone,
    command: "create", // Explicitly add command to match what backend expects
  };

  try {
    // First attempt
    console.log("[CreateReplay] Attempting to POST /api/replay/create");
    const response = await axios.post("/api/replay/create", replay);
    console.log(
      "[CreateReplay] Create replay API request successful (Attempt 1)"
    );

    // Introduce a small delay to ensure request propagation
    await new Promise((resolve) => setTimeout(resolve, 500));
    return response;
  } catch (error) {
    // Error handling logic
    if (axios.isAxiosError(error)) {
      console.error(
        "[CreateReplay] First attempt failed (AxiosError):",
        error.response?.status,
        error.response?.data || error.message
      );
    } else if (error instanceof Error) {
      console.error(
        "[CreateReplay] First attempt failed (Error):",
        error.message
      );
    } else {
      console.error("[CreateReplay] First attempt failed (Unknown):", error);
    }

    // Wait and retry
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      // Second attempt
      console.log("[CreateReplay] Retrying POST /api/replay/create");
      const retryResponse = await axios.post("/api/replay/create", replay);
      console.log("[CreateReplay] Retry successful (Attempt 2)");
      return retryResponse;
    } catch (retryError) {
      // Handle retry error
      if (axios.isAxiosError(retryError)) {
        console.error(
          "[CreateReplay] Retry failed (AxiosError):",
          retryError.response?.status,
          retryError.response?.data || retryError.message
        );
      } else if (retryError instanceof Error) {
        console.error(
          "[CreateReplay] Retry failed (Error):",
          retryError.message
        );
      } else {
        console.error("[CreateReplay] Retry failed (Unknown):", retryError);
      }
      throw retryError; // Re-throw for caller to handle
    }
  }
}

// Updated to ensure dates are snapped to minutes
export async function getReplayInfo(start: DateTime, end: DateTime) {
  const snappedStart = start.startOf("minute");
  const snappedEnd = end.startOf("minute");
  return await axios.get("/api/replay/info", {
    params: { start: snappedStart.toISO(), end: snappedEnd.toISO() },
  });
}

async function getBookingQueries(start: DateTime, end: DateTime) {
  return await axios.post("/api/generateReport", {
    start: start.toISO(),
    end: end.toISO(),
    timezone: "",
    reportType: "Booking Table Replay",
  });
}

async function getBarrierRequestQueries(start: DateTime, end: DateTime) {
  return await axios.post("/api/generateReport", {
    start: start.toISO(),
    end: end.toISO(),
    timezone: "",
    reportType: "Barrier Requests Table Replay",
  });
}
